import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Input, Switch, Tooltip } from 'antd'

import * as Rules from 'admin/lib/ValidationRules'
import { DualTimeZoneDateTimePicker } from 'admin/pages/RecordEdit/common/DualTimeZoneDateTimePicker'

import {
  AutoCompleteFormItem,
  FormItem,
  FormRowWithValidation,
  FormSectionTitle,
  FormSpace,
  SectionProps,
  UnstyledSection,
  UnstyledSectionWrapper
} from 'admin/lib/Forms'
import { Flag } from 'admin/lib/Icons/Flag'

import { usDateFormatter } from 'admin/lib/Functions/timeZoneHelper'
import { Delivery as fields } from 'admin/lib/Records/fields'

import { SupplierSelect } from './SupplierSelect'
import { useDateAutoCopy } from 'admin/pages/RecordEdit/common/useDateAutoCopy'
import { KnownAddressInput } from 'admin/pages/RecordEdit/common/KnownAddressInput'
import {
  RAW,
  ENRICHED,
  ENRICHED_DISABLED_MESSAGE
} from 'admin/pages/RecordEdit/common/constants'
import {
  isDisabled,
  isHidden
} from 'admin/pages/RecordEdit/common/rawEnrichedHelpers'
import {
  TitleBar,
  ShowRawWrapper,
  DataToggle,
  DataToggleItem
} from 'admin/pages/RecordEdit/common/styledComponents'

import {
  CheckConsignmentOrDeliveryNumber,
  CheckReceiverOrSupplierOrSenderOrCarrierReference,
  CheckTradeContractor,
  CheckVehicleReg
} from 'admin/lib/SimpleDataValidation'
import {
  calculateWarningsForArrivalDate,
  calculateWarningsForExitDate
} from 'admin/pages/RecordEdit/common/dateValidation'

type Props = {
  createdAt: string
  projectName: string
  projectCountry: string
  projectTimeZone: string
  projectId: string
  isImperial: boolean
  hasOneOrMoreLineItemWithProductGroupsContainingConcrete: boolean
  dataView: string
  showRaw: boolean
  enrichedDisabled: boolean
  setDataView: (v: string) => void
  setShowRaw: () => void
  setEnrichedDisabled: (v: boolean) => void
  attachmentFileNames: string[]
} & Omit<SectionProps, 'children'>

export const DeliveryData = (props: Props) => {
  const form = useFormContext()
  const dirtyFields = form.formState.dirtyFields

  // Register non-interactive fields set from setValue
  useEffect(() => {
    form.register(fields.enriched.deliveryAddressEntered.path)
    form.register(fields.enriched.deliveryAddressCoordinates.path)
    form.register(fields.enriched.dispatchAddressEntered.path)
    form.register(fields.enriched.dispatchAddressCoordinates.path)
  }, [form])

  const journeyDistanceMiles = form.watch('enriched.journey.distanceMiles')

  const exitDatePopulation = useDateAutoCopy(
    fields.enriched.deliveryArrivalDate.path,
    fields.enriched.deliveryExitDate.path
  )

  const [arrivalDateWarningMessages, setArrivalDateWarningMessages] = useState<
    string[]
  >([])
  const [exitDateWarningMessages, setExitDateWarningMessages] = useState<
    string[]
  >([])

  const validateArrivalDate = (value: any) => {
    setArrivalDateWarningMessages(
      calculateWarningsForArrivalDate(
        value,
        props.createdAt,
        props.projectTimeZone
      )
    )
    // Trigger Exit date validation too:
    validateExitDate(form.getValues(fields.enriched.deliveryExitDate.path))
    // Always return TRUE so we do not block form submission
    return true
  }

  const validateExitDate = (value: any) => {
    setExitDateWarningMessages(
      calculateWarningsForExitDate(
        value,
        form.getValues(fields.enriched.deliveryArrivalDate.path),
        props.createdAt,
        props.projectTimeZone,
        props.hasOneOrMoreLineItemWithProductGroupsContainingConcrete
      )
    )
    // Always return TRUE so we do not block form submission
    return true
  }

  useEffect(() => {
    let sub1: { unsubscribe: () => void }, sub2: { unsubscribe: () => void }
    if (props.isImperial) {
      sub1 = form.watch('enriched.deliveryArrivalDate')
      sub2 = form.watch('enriched.deliveryExitDate')
      form.setValue(
        'enriched.deliveryArrivalDateUS',
        usDateFormatter(form.getValues('enriched.deliveryArrivalDate'))
      )
      form.setValue(
        'enriched.deliveryExitDateUS',
        usDateFormatter(form.getValues('enriched.deliveryExitDate'))
      )
    }
    return () => {
      if (sub1 && sub1.unsubscribe) {
        sub1.unsubscribe()
      }
      if (sub2 && sub2.unsubscribe) {
        sub2.unsubscribe()
      }
    }
  }, [form, props.isImperial])

  useEffect(() => {
    if (dirtyFields && dirtyFields.raw) {
      props.setEnrichedDisabled(true)
    }
  }, [dirtyFields, props])

  return (
    <UnstyledSectionWrapper className="UnstyledSectionWrapper">
      <UnstyledSection {...props}>
        {({ firstInputRef }) => (
          <>
            <TitleBar>
              <FormSectionTitle level={5}>
                <Flag country={props.projectCountry} /> Delivery
              </FormSectionTitle>
              {props.dataView === ENRICHED && (
                <ShowRawWrapper>
                  <Switch
                    checked={props.showRaw}
                    onChange={props.setShowRaw}
                    size="small"
                  />
                  <div style={{ marginTop: '1px' }}>Show Raw</div>
                </ShowRawWrapper>
              )}
              <DataToggle>
                <DataToggleItem
                  className={props.dataView === RAW ? 'active' : ''}
                  onClick={() => {
                    performance.mark('raw-enriched-tab-switch', {
                      detail: { context: 'delivery', destination: RAW }
                    })
                    props.setDataView(RAW)
                  }}
                >
                  Raw
                </DataToggleItem>
                <DataToggleItem
                  className={
                    props.dataView === ENRICHED
                      ? 'active'
                      : props.enrichedDisabled
                        ? 'disabled'
                        : ''
                  }
                  onClick={() => {
                    if (!props.enrichedDisabled) {
                      performance.mark('raw-enriched-tab-switch', {
                        detail: { context: 'delivery', destination: ENRICHED }
                      })
                      props.setDataView(ENRICHED)
                    }
                  }}
                >
                  <Tooltip
                    title={
                      props.enrichedDisabled ? ENRICHED_DISABLED_MESSAGE : ''
                    }
                    placement="right"
                    mouseLeaveDelay={0.5}
                  >
                    Enriched
                  </Tooltip>
                </DataToggleItem>
              </DataToggle>
            </TitleBar>
            <FormSpace />

            <FormRowWithValidation>
              <FormItem
                label={fields.raw.deliveryArrivalDate.label}
                name={fields.raw.deliveryArrivalDate.path}
                disabled={isDisabled(
                  fields.raw.deliveryArrivalDate.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                hidden={isHidden(
                  fields.raw.deliveryArrivalDate.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                inputRef={props.dataView === RAW ? firstInputRef : null}
                instrumented={true}
              />

              <FormItem
                label={fields.raw.deliveryArrivalTime.label}
                name={fields.raw.deliveryArrivalTime.path}
                disabled={isDisabled(
                  fields.raw.deliveryArrivalTime.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                hidden={isHidden(
                  fields.raw.deliveryArrivalTime.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                instrumented={true}
              />
            </FormRowWithValidation>
            <DualTimeZoneDateTimePicker
              id="DeliveryDataArrivalDate"
              label={fields.enriched.deliveryArrivalDate.label}
              name={fields.enriched.deliveryArrivalDate.path}
              imperialName={fields.enriched.deliveryArrivalDateUS.path}
              isImperial={props.isImperial}
              isDisabled={isDisabled(
                fields.enriched.deliveryArrivalDate.isRawField,
                props.dataView,
                props.showRaw
              )}
              isHidden={isHidden(
                fields.enriched.deliveryArrivalDate.isRawField,
                props.dataView,
                props.showRaw
              )}
              form={form}
              firstInputRef={props.dataView !== RAW ? firstInputRef : null}
              validateDate={validateArrivalDate}
              warningMessages={arrivalDateWarningMessages}
              instrumented={true}
            />

            <FormRowWithValidation>
              <FormItem
                label={fields.raw.deliveryExitDate.label}
                name={fields.raw.deliveryExitDate.path}
                disabled={isDisabled(
                  fields.raw.deliveryExitDate.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                hidden={isHidden(
                  fields.raw.deliveryExitDate.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                instrumented={true}
              />

              <FormItem
                label={fields.raw.deliveryExitTime.label}
                name={fields.raw.deliveryExitTime.path}
                disabled={isDisabled(
                  fields.raw.deliveryExitTime.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                hidden={isHidden(
                  fields.raw.deliveryExitTime.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                instrumented={true}
              />
            </FormRowWithValidation>
            <DualTimeZoneDateTimePicker
              id="DeliveryDataExitDate"
              label={fields.enriched.deliveryExitDate.label}
              name={fields.enriched.deliveryExitDate.path}
              imperialName={fields.enriched.deliveryExitDateUS.path}
              isImperial={props.isImperial}
              isDisabled={isDisabled(
                fields.enriched.deliveryExitDate.isRawField,
                props.dataView,
                props.showRaw
              )}
              isHidden={isHidden(
                fields.enriched.deliveryExitDate.isRawField,
                props.dataView,
                props.showRaw
              )}
              form={form}
              validateDate={validateExitDate}
              warningMessages={exitDateWarningMessages}
              onFocus={() => {
                exitDatePopulation.onFocus()
                form.trigger(fields.enriched.deliveryExitDate.path)
              }}
              instrumented={true}
            />

            <FormItem
              label={fields.raw.deliveryId.label}
              name={fields.raw.deliveryId.path}
              disabled={isDisabled(
                fields.raw.deliveryId.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.deliveryId.isRawField,
                props.dataView,
                props.showRaw
              )}
              instrumented={true}
            />
            <FormItem
              label={fields.enriched.deliveryId.label}
              name={fields.enriched.deliveryId.path}
              id="deliveryDataDeliveryId"
              disabled={isDisabled(
                fields.enriched.deliveryId.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.enriched.deliveryId.isRawField,
                props.dataView,
                props.showRaw
              )}
              rules={
                isHidden(
                  fields.enriched.deliveryId.isRawField,
                  props.dataView,
                  props.showRaw
                )
                  ? null
                  : Rules.MandatoryString
              }
              simpleDataValidationRules={v =>
                CheckConsignmentOrDeliveryNumber(v, props.attachmentFileNames)
              }
              instrumented={true}
            />

            <FormItem
              label={fields.raw.deliveryAddress.label}
              name={fields.raw.deliveryAddress.path}
              disabled={isDisabled(
                fields.raw.deliveryAddress.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.deliveryAddress.isRawField,
                props.dataView,
                props.showRaw
              )}
              instrumented={true}
            >
              <Input.TextArea autoSize rows={1} />
            </FormItem>
            <FormItem
              label={fields.enriched.deliveryAddress.label}
              name={fields.enriched.deliveryAddress.path}
              id="deliveryDataDeliveryAddress"
              disabled={isDisabled(
                fields.enriched.deliveryAddress.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={true}
            >
              <Input.TextArea autoSize rows={1} />
            </FormItem>

            {journeyDistanceMiles > 1000 && (
              <p className="validation-warning">
                Calculated journey distance exceeds recommended limit. Please
                confirm addresses are correct.
              </p>
            )}

            <KnownAddressInput
              form={form}
              addressName="Delivery"
              knownlabel={fields.enriched.deliveryAddressKnown.label}
              knownPropertyPath={fields.enriched.deliveryAddressKnown.path}
              givenPropertyPath={fields.enriched.deliveryAddress.path}
              enteredPropertyPath={fields.enriched.deliveryAddressEntered.path}
              coordinatesPropertyPath={
                fields.enriched.deliveryAddressCoordinates.path
              }
              disabled={isDisabled(
                fields.enriched.deliveryAddressKnown.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.enriched.deliveryAddressKnown.isRawField,
                props.dataView,
                props.showRaw
              )}
              projectCountry={props.projectCountry}
              instrumented={true}
            />

            <FormItem
              label={fields.raw.dispatchAddress.label}
              name={fields.raw.dispatchAddress.path}
              disabled={isDisabled(
                fields.raw.dispatchAddress.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.dispatchAddress.isRawField,
                props.dataView,
                props.showRaw
              )}
              instrumented={true}
            >
              <Input.TextArea autoSize rows={1} />
            </FormItem>
            <FormItem
              label={fields.enriched.givenDispatchAddress.label}
              name={fields.enriched.givenDispatchAddress.path}
              id="deliveryDataDispatchAddress"
              disabled={isDisabled(
                fields.enriched.givenDispatchAddress.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={true}
            >
              <Input.TextArea autoSize rows={1} />
            </FormItem>

            <KnownAddressInput
              form={form}
              addressName="Dispatch"
              knownlabel={fields.enriched.dispatchAddressKnown.label}
              knownPropertyPath={fields.enriched.dispatchAddressKnown.path}
              givenPropertyPath={fields.enriched.givenDispatchAddress.path}
              enteredPropertyPath={fields.enriched.dispatchAddressEntered.path}
              coordinatesPropertyPath={
                fields.enriched.dispatchAddressCoordinates.path
              }
              disabled={isDisabled(
                fields.enriched.dispatchAddressKnown.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.enriched.dispatchAddressKnown.isRawField,
                props.dataView,
                props.showRaw
              )}
              projectCountry={props.projectCountry}
              instrumented={true}
            />

            <FormItem
              label={fields.raw.tradeContractor.label}
              name={fields.raw.tradeContractor.path}
              disabled={isDisabled(
                fields.raw.tradeContractor.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.tradeContractor.isRawField,
                props.dataView,
                props.showRaw
              )}
              instrumented={true}
            />
            <AutoCompleteFormItem
              label={fields.enriched.tradeContractor.label}
              name={fields.enriched.tradeContractor.path}
              id="deliveryDataTradeContractor"
              disabled={isDisabled(
                fields.enriched.tradeContractor.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.enriched.tradeContractor.isRawField,
                props.dataView,
                props.showRaw
              )}
              simpleDataValidationRules={v =>
                CheckTradeContractor(v, props.projectName)
              }
              instrumented={true}
            />

            <FormSpace />

            <SupplierSelect
              dataView={props.dataView}
              showRaw={props.showRaw}
              isDisabled={isDisabled}
              isHidden={isHidden}
            />

            <FormItem
              label={fields.raw.vehicleRegistration.label}
              name={fields.raw.vehicleRegistration.path}
              disabled={isDisabled(
                fields.raw.vehicleRegistration.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.vehicleRegistration.isRawField,
                props.dataView,
                props.showRaw
              )}
              uppercaseInput={true}
              instrumented={true}
            />
            <AutoCompleteFormItem
              label={fields.enriched.vehicleRegistration.label}
              name={fields.enriched.vehicleRegistration.path}
              id="deliveryDataVehicleReg"
              disabled={isDisabled(
                fields.enriched.vehicleRegistration.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.enriched.vehicleRegistration.isRawField,
                props.dataView,
                props.showRaw
              )}
              simpleDataValidationRules={CheckVehicleReg}
              uppercaseInput={true}
              instrumented={true}
            />

            <FormSpace />

            <FormItem
              label={fields.raw.receiverReferenceNumber.label}
              name={fields.raw.receiverReferenceNumber.path}
              disabled={isDisabled(
                fields.raw.receiverReferenceNumber.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.receiverReferenceNumber.isRawField,
                props.dataView,
                props.showRaw
              )}
              instrumented={true}
            />
            <AutoCompleteFormItem
              label={fields.enriched.receiverReferenceNumber.label}
              name={fields.enriched.receiverReferenceNumber.path}
              id="deliveryDataReceiverReference"
              disabled={isDisabled(
                fields.enriched.receiverReferenceNumber.isRawField,
                props.dataView,
                props.showRaw
              )}
              // Always hidden in enriched view
              hidden={true}
              simpleDataValidationRules={
                CheckReceiverOrSupplierOrSenderOrCarrierReference
              }
              instrumented={true}
            />

            <FormItem
              label={fields.raw.supplierReferenceNumber.label}
              name={fields.raw.supplierReferenceNumber.path}
              disabled={isDisabled(
                fields.raw.supplierReferenceNumber.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.supplierReferenceNumber.isRawField,
                props.dataView,
                props.showRaw
              )}
              instrumented={true}
            />
            <AutoCompleteFormItem
              label={fields.enriched.supplierReferenceNumber.label}
              name={fields.enriched.supplierReferenceNumber.path}
              id="deliveryDataSupplierReference"
              disabled={isDisabled(
                fields.enriched.supplierReferenceNumber.isRawField,
                props.dataView,
                props.showRaw
              )}
              // Always hidden in enriched view
              hidden={true}
              simpleDataValidationRules={
                CheckReceiverOrSupplierOrSenderOrCarrierReference
              }
              instrumented={true}
            />

            {/* // Notes are always hidden */}
            <AutoCompleteFormItem
              label={fields.enriched.notes.label}
              name={fields.enriched.notes.path}
              id="deliveryDataNotes"
              disabled={isDisabled(
                fields.enriched.notes.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={true}
              instrumented={true}
            >
              <Input.TextArea autoSize rows={1} />
            </AutoCompleteFormItem>
          </>
        )}
      </UnstyledSection>
    </UnstyledSectionWrapper>
  )
}
